/**
 * Rental Status Utilities
 * Calculate if a rental is approaching or past its max duration
 */

export const RENTAL_STATUS = {
  NORMAL: 'NORMAL',
  WARNING: 'WARNING',
  OVERDUE: 'OVERDUE'
};

/**
 * Calculate the rental status based on current time and settings
 * @param {Object} reservation - The reservation object
 * @param {number} maxRentalDurationHours - Max rental duration from settings (in hours)
 * @param {number} notificationFlagMinutes - Notification flag from settings (in minutes)
 * @returns {string} RENTAL_STATUS - NORMAL, WARNING, or OVERDUE
 */
export const calculateRentalStatus = (reservation, maxRentalDurationHours, notificationFlagMinutes) => {
  // Only calculate for ACTIVE reservations
  if (reservation.status !== 'ACTIVE') {
    return RENTAL_STATUS.NORMAL;
  }

  // If settings are not configured or are 0, treat as normal
  if (!maxRentalDurationHours || maxRentalDurationHours === 0) {
    return RENTAL_STATUS.NORMAL;
  }

  // Parse the start time
  const startTime = new Date(reservation.startTime + (reservation.startTime.includes('Z') ? '' : 'Z'));
  const currentTime = new Date();

  // Calculate the elapsed time in milliseconds
  const elapsedMs = currentTime - startTime;
  const elapsedHours = elapsedMs / (1000 * 60 * 60);

  // Calculate when the warning should trigger (max duration - notification flag)
  const warningThresholdHours = maxRentalDurationHours - (notificationFlagMinutes / 60);

  // Check if overdue (past max rental duration)
  if (elapsedHours >= maxRentalDurationHours) {
    return RENTAL_STATUS.OVERDUE;
  }

  // Check if in warning zone (past warning threshold but before max duration)
  if (notificationFlagMinutes > 0 && elapsedHours >= warningThresholdHours) {
    return RENTAL_STATUS.WARNING;
  }

  // Otherwise, normal status
  return RENTAL_STATUS.NORMAL;
};

/**
 * Get the border color class based on rental status
 * @param {string} status - RENTAL_STATUS value
 * @returns {string} Tailwind border color classes
 */
export const getBorderColorClass = (status) => {
  switch (status) {
    case RENTAL_STATUS.WARNING:
      return 'border-yellow-400 border-2';
    case RENTAL_STATUS.OVERDUE:
      return 'border-red-500 border-2';
    case RENTAL_STATUS.NORMAL:
    default:
      return 'border-gray-200';
  }
};

/**
 * Get human-readable status description
 * @param {string} status - RENTAL_STATUS value
 * @param {Object} reservation - The reservation object
 * @param {number} maxRentalDurationHours - Max rental duration from settings
 * @param {number} notificationFlagMinutes - Notification flag from settings
 * @returns {string|null} Description or null if normal
 */
export const getStatusDescription = (status, reservation, maxRentalDurationHours, notificationFlagMinutes) => {
  if (status === RENTAL_STATUS.NORMAL) {
    return null;
  }

  const startTime = new Date(reservation.startTime + (reservation.startTime.includes('Z') ? '' : 'Z'));
  const currentTime = new Date();
  const elapsedMs = currentTime - startTime;
  const elapsedHours = elapsedMs / (1000 * 60 * 60);

  if (status === RENTAL_STATUS.OVERDUE) {
    const overdueHours = (elapsedHours - maxRentalDurationHours).toFixed(1);
    return `⚠️ OVERDUE by ${overdueHours} hours (Max: ${maxRentalDurationHours}h)`;
  }

  if (status === RENTAL_STATUS.WARNING) {
    const remainingHours = (maxRentalDurationHours - elapsedHours).toFixed(1);
    return `⏰ Approaching max duration - ${remainingHours}h remaining (Max: ${maxRentalDurationHours}h)`;
  }

  return null;
};
